"use client"

import * as React from "react"
import { Dialog as SheetPrimitive } from "@base-ui/react/dialog"

import { cn } from "../lib/utils"
import { popSheetOpen, pushSheetOpen } from "../lib/sheet-state"
import { Button } from "./button"
import { XIcon } from "lucide-react"

// `pushSheetOpen` / `popSheetOpen` are imported but only used inside the
// `Sheet` root effect — kept out of `SheetContent` because that component
// is mounted by the Portal regardless of open state, which would leave
// the global count stuck.

// Opt-in scroll-edge fades. `SheetContent scrollFade` turns on a soft fade
// under the header, above a pinned footer, and at the bottom edge (plus bottom
// clearance on the scroll body). Off by default so every existing sheet is
// unchanged; the header/footer read this to include their own matching fade.
const ScrollFadeContext = React.createContext(false)

function Sheet({
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: SheetPrimitive.Root.Props) {
  // Track the *effective* open state so consumers of `useAnySheetOpen()`
  // (mobile chrome that needs to step aside, etc.) only see the count
  // increment while the sheet is genuinely open. Supports both controlled
  // (`open={…}`) and uncontrolled (`defaultOpen`) usage by mirroring the
  // primitive's open-change events into local state.
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  )
  const isControlled = open !== undefined
  const effectiveOpen = isControlled ? Boolean(open) : uncontrolledOpen

  React.useEffect(() => {
    if (!effectiveOpen) return
    pushSheetOpen()
    return () => popSheetOpen()
  }, [effectiveOpen])

  return (
    <SheetPrimitive.Root
      data-slot="sheet"
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={(next, ...rest) => {
        if (!isControlled) setUncontrolledOpen(Boolean(next))
        onOpenChange?.(next, ...rest)
      }}
      {...props}
    />
  )
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      // base-ui suppresses the backdrop on NESTED dialogs (a sheet opened
      // from inside another open sheet renders no dim/blur at all). Our
      // Sheet owns that policy explicitly via the `backdrop` prop on
      // SheetContent, so force the backdrop to render regardless of
      // nesting — callers stacking sheets opt out with `backdrop={false}`.
      forceRender
      className={cn(
        // Duration matched to SheetContent's own transition below (was
        // 150ms/ease-in-out here vs the sheet's own timing - the backdrop
        // used to finish fading before/after the sheet finished sliding,
        // reading as disjointed rather than one motion) - 2026-09-05.
        "fixed inset-0 z-50 bg-black/10 transition-opacity duration-[400ms] ease-[cubic-bezier(0.32,0.72,0,1)] md:duration-[250ms] data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-md",
        className,
      )}
      {...props}
    />
  )
}

// Desktop max-widths for the `size` prop. Applied as an inline `maxWidth`
// (not a Tailwind `max-w-[…]` utility) because connect runs Tailwind v4 over a
// v3-style config, where arbitrary `max-w` utilities don't reliably compile —
// inline style sidesteps that entirely. On mobile the sheet stays full-screen
// (`max-sm:!max-w-none` overrides the inline cap below the `sm` breakpoint).
const SHEET_SIZES = {
  sm: "28rem",
  md: "42rem",
  lg: "56rem",
  xl: "72rem",
  full: "none",
} as const

function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  closeButtonClassName,
  closeButtonVariant = "solid",
  backdrop = true,
  size,
  mobileVariant = "full",
  mobilePadded = true,
  style,
  // Base UI focuses the first tabbable element on open by default, which
  // lands on the first `Input` in a form-sheet — popping the mobile keyboard
  // and showing a premature focus ring. Default to `false` ("do not move
  // focus") so the sheet opens calm; keyboard users can still Tab in and the
  // modal focus trap stays active. Callers can pass `initialFocus` (a ref, or
  // `true` for default behaviour) to opt a specific sheet back in.
  initialFocus = false,
  scrollFade = false,
  keepMounted = false,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: "top" | "right" | "bottom" | "left"
  showCloseButton?: boolean
  /**
   * Keep the sheet's content in the DOM after it closes, instead of base-ui's
   * default tear-down-and-remount. Mirrors `DialogContent`'s own prop of the
   * same name — same use case: content whose remount is expensive (an
   * attached third-party SDK element, an iframe that re-fetches on mount),
   * reopened repeatedly. Default `false`.
   */
  keepMounted?: boolean
  /**
   * Opt in to scroll-edge fades: content softly dissolves under the header,
   * above a pinned footer, and at the bottom edge, with bottom clearance on the
   * scroll body. Off by default — existing sheets are unchanged.
   */
  scrollFade?: boolean
  /** Extra styling for the built-in close control. */
  closeButtonClassName?: string
  /**
   * How the built-in close (X) is painted.
   *
   * `solid` (default, 2026-09-05) gives it a filled surface + hairline - a
   * plain transparent icon read as too easy to miss/mis-tap in practice,
   * even over empty padding.
   *
   * `ghost` is the transparent icon button - a plain icon with no surface
   * of its own. Rarely the right call now; kept for a caller that's
   * deliberately chosen it (or explicitly wants the OLD default back).
   */
  closeButtonVariant?: "ghost" | "solid"
  /**
   * Desktop width preset. `sm`–`xl` cap the floating sheet at a fixed
   * max-width; `full` spans the whole viewport (edge-to-edge). On mobile
   * every size is full-screen. Omit to keep the default narrow sheet.
   */
  size?: "sm" | "md" | "lg" | "xl" | "full"
  /**
   * How the sheet presents BELOW `sm`.
   *
   * `full` (default) is the app-wide behaviour: a full-screen page, edge to
   * edge, sharp corners, whichever side it enters from.
   *
   * `partial` keeps it docked to the bottom edge as a card — content height,
   * capped at 85dvh, rounded TOP corners only, with the page still visible
   * behind it. Use it for a short, focused task (a menu item, a picker) where
   * taking over the whole screen reads heavier than the job. Pair with
   * `side="bottom"` and give the body its own scroller
   * (`min-h-0 flex-1 overflow-y-auto`) so long content scrolls inside the cap.
   *
   * The cap is a variable, so a sheet whose content is genuinely taller can
   * raise it without fighting the `!important` variant class for specificity:
   * `style={{ '--sheet-partial-max-h': '92dvh' }}`. Keep some page showing —
   * that strip is what says "this is a card over the list", not a new screen.
   *
   * Gets the same outer margin as the desktop floating card by default
   * (`mobilePadded`, below) — rounded on every corner, not flush against the
   * screen's edges.
   */
  mobileVariant?: "full" | "partial"
  /**
   * Whether `mobileVariant="partial"` gets the same outer margin as the
   * desktop floating card (`inset-x-4 bottom-4`, rounded on every corner)
   * rather than sitting flush against the screen's left/right/bottom edges.
   * Default `true` - flush-to-the-edge read as cramped with no breathing
   * room round it (confirmed live 2026-09-05). Set `false` for a caller that
   * genuinely wants the old edge-to-edge dock instead. No effect on
   * `mobileVariant="full"`, which is already intentionally edge-to-edge.
   */
  mobilePadded?: boolean
  /**
   * Whether to render the overlay (the dimmed + blurred
   * backdrop). Default `true`.
   *
   * Set to `false` when the sheet is opened on top of another
   * modal that already has its own overlay (e.g. a Dialog) — a
   * second overlay would double the dim and stack two blur
   * filters, AND the overlay's outside-click handling can race
   * with the parent dialog's. Disabling lets the sheet float
   * over the existing modal cleanly.
   */
  backdrop?: boolean
}) {
  return (
    <ScrollFadeContext.Provider value={scrollFade}>
      <SheetPortal keepMounted={keepMounted}>
        {backdrop ? <SheetOverlay /> : null}
        <SheetPrimitive.Popup
          data-slot="sheet-content"
          data-side={side}
          initialFocus={initialFocus}
          // Inline styles for `--background` / `--foreground` because
          // connect runs Tailwind v4 with `@config tailwind.config.js`
          // pointed at a v3-style config that wraps tokens in
          // `hsl(var(--background))`. Tailwind-v4's tokens (which the
          // `.new-ui-theme` scope defines) are full colours like
          // `#f7f1e7`, not HSL components — so the v3 utility resolves
          // to `hsl(#f7f1e7)` (invalid) and the popup's background
          // collapses to transparent, exposing whatever sits behind it
          // as a "teal" colour. Using `var()` directly in inline styles
          // sidesteps the `hsl()` wrapper entirely and works with both
          // token formats the consumer might define.
          style={
            {
              backgroundColor: "var(--background)",
              color: "var(--foreground)",
              // Scroll-fade gradients (used by `scrollFade`). Defined in plain
              // CSS here — not a Tailwind arbitrary gradient — so we can use an
              // EASED multi-stop curve that fades to the SAME colour at zero
              // alpha (`rgb(from … / 0)`). A plain `…, transparent` fades toward
              // transparent *black*, which casts grey over light surfaces and
              // makes the soft end read as a hard line; same-colour-zero-alpha +
              // easing removes both. The header/footer inherit these vars.
              "--sheet-fade-down":
                "linear-gradient(to bottom, var(--background) 0%, var(--background) 12%, rgb(from var(--background) r g b / 0.6) 46%, rgb(from var(--background) r g b / 0.14) 78%, rgb(from var(--background) r g b / 0) 100%)",
              "--sheet-fade-up":
                "linear-gradient(to top, var(--background) 0%, var(--background) 12%, rgb(from var(--background) r g b / 0.6) 46%, rgb(from var(--background) r g b / 0.14) 78%, rgb(from var(--background) r g b / 0) 100%)",
              // `size` (other than `full`) caps the desktop width via inline
              // maxWidth; mobile ignores it via `max-sm:!max-w-none`.
              ...(size && size !== "full"
                ? { maxWidth: SHEET_SIZES[size] }
                : {}),
              // Caller `style` wins last (and is merged, so it no longer wipes
              // the background/colour the way a spread `{...props}` style did).
              ...style,
            } as React.CSSProperties & Record<`--${string}`, string>
          }
          className={cn(
            // `new-ui-theme` keeps the var resolution scope correct
            // through the portal. See the comment block for the colour
            // workaround above.
            //
            // Floating-sheet defaults: every side carries 1rem of
            // viewport margin, `rounded-2xl` corners, and a 250ms
            // slide on desktop / 400ms on mobile (mobile sheets
            // travel further on a smaller viewport and benefit
            // from a touch more time), eased on iOS's own sheet
            // curve (cubic-bezier(0.32,0.72,0,1), per vaul/Ionic)
            // rather than ease-in-out, so it settles instead of
            // decelerating symmetrically like a bounce. Per-side
            // borders dropped because the rounded card already
            // separates from the backdrop.
            //
            // `overflow-hidden` BECAUSE IT IS ALWAYS ROUNDED. Corners are
            // drawn on this element, but nothing clipped to them, so any
            // child with a background of its own painted straight over
            // them: a sheet with a header or a sticky footer showed square
            // white corners poking out past the curve. The scrolling is
            // always on an inner element, so there is nothing here that
            // wanted to overflow, and popovers portal to the body.
            "new-ui-theme fixed z-50 flex flex-col gap-4 overflow-hidden rounded-2xl bg-clip-padding text-sm shadow-lg transition duration-[400ms] ease-[cubic-bezier(0.32,0.72,0,1)] md:duration-[250ms] data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:inset-x-4 data-[side=bottom]:bottom-4 data-[side=bottom]:h-auto data-[side=bottom]:data-ending-style:translate-y-[2.5rem] data-[side=bottom]:data-starting-style:translate-y-[2.5rem] data-[side=left]:inset-0 data-[side=left]:w-full data-[side=left]:max-sm:rounded-none data-[side=left]:sm:inset-y-4 data-[side=left]:sm:left-4 data-[side=left]:sm:right-auto data-[side=left]:sm:h-auto data-[side=left]:sm:w-3/4 data-[side=left]:data-ending-style:translate-x-[-2.5rem] data-[side=left]:data-starting-style:translate-x-[-2.5rem] data-[side=right]:inset-0 data-[side=right]:w-full data-[side=right]:max-sm:rounded-none data-[side=right]:sm:inset-y-4 data-[side=right]:sm:right-4 data-[side=right]:sm:left-auto data-[side=right]:sm:h-auto data-[side=right]:sm:w-3/4 data-[side=right]:data-ending-style:translate-x-[2.5rem] data-[side=right]:data-starting-style:translate-x-[2.5rem] data-[side=top]:inset-x-4 data-[side=top]:top-4 data-[side=top]:h-auto data-[side=top]:data-ending-style:translate-y-[-2.5rem] data-[side=top]:data-starting-style:translate-y-[-2.5rem] data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm",
            // ⭐ Mobile default: EVERY sheet is full-page edge-to-edge,
            // whichever side it enters from — no floating margin, no
            // rounding, full width AND height, and any caller max-height
            // cap dropped. Below `sm` a sheet is a full screen, not a card.
            // `!important` because the per-side `data-[side=…]:` classes
            // (and inline `maxWidth`) out-specify a plain utility; the
            // desktop (`sm:`) floating card above is untouched. The
            // entrance/exit translate still animates it in from its side.
            mobileVariant === "full" &&
              "max-sm:!inset-0 max-sm:!m-0 max-sm:!h-full max-sm:!max-h-none max-sm:!w-full max-sm:!max-w-none max-sm:!rounded-none",
            // `partial`: docked near the bottom edge instead of owning the
            // screen. Content height (`h-auto`) capped at 85dvh so the page
            // stays visible above it. Same `!important` reasoning as the
            // block above — the per-side `data-[side=…]` rules and the
            // inline maxWidth out-specify plain utilities.
            //
            // The cap reads a variable so a caller can raise it inline
            // (`--sheet-partial-max-h`) instead of trying to out-specify an
            // `!important` arbitrary value with another one.
            mobileVariant === "partial" &&
              (mobilePadded
                ? // Default: a MINIMAL outer margin (`inset-x-2` - 8px,
                  // nudged up a hairline from 6px - side to side, `bottom-2`
                  // - 8px - off the bottom edge) - closer to how iOS's own
                  // system sheets (Apple Pay, share sheet)
                  // sit just off the physical screen edge rather than
                  // floating with a large gap (was `bottom-10`, sat too high
                  // to read as hugging the device's own corner - 2026-09-05,
                  // confirmed against an actual Apple Pay sheet screenshot).
                  // Radius is asymmetric to match that same reference:
                  // smaller on top (`rounded-t-2xl`, 16px - floating in open
                  // space mid-screen, no device curve to match) and bigger
                  // on the bottom - `rounded-b-[45px]`, not the design
                  // system's own largest named token (`4xl`, 28px, tried
                  // first but read as still not rounded enough). Started
                  // from the ACTUAL iPhone 15 Pro display corner radius
                  // (55px, confirmed via Apple's own published specs), eased
                  // down (55 -> 48 -> 44 -> 40) then nudged back up in small
                  // steps (40 -> 42 -> 45) from that exact figure - paired
                  // with the small gap above so it reads as flowing into
                  // the screen's own corner rather than a symmetric
                  // floating card. `pb-4` ADDED on top of whatever bottom
                  // padding the sheet's own header/footer/form content
                  // already carries (2026-09-05) - a radius this big eats
                  // visibly into a full-width bottom-pinned button (a
                  // "Pay"/"Continue" CTA) that sits flush with the
                  // container's own bottom edge, reading as clipped/too
                  // close rather than a deliberate curve. This is a safety
                  // margin at the OUTER container level, not something
                  // each caller's own content has to remember to add.
                  // Width left to the browser to compute from the insets
                  // (no explicit `w-*`, matching the base
                  // `data-[side=bottom]` rule below, which does the same on
                  // desktop) rather than fighting them with `w-full`.
                  "max-sm:!inset-x-2 max-sm:!top-auto max-sm:!bottom-2 max-sm:!m-0 max-sm:!h-auto max-sm:!max-h-[var(--sheet-partial-max-h,85dvh)] max-sm:!max-w-none max-sm:!rounded-t-2xl max-sm:!rounded-b-[45px] max-sm:!pb-4"
                : // `mobilePadded={false}`: flush against the screen's
                  // left/right/bottom edges, full width, rounding on the TOP
                  // corners only since the bottom two are off-screen.
                  "max-sm:!inset-x-0 max-sm:!top-auto max-sm:!bottom-0 max-sm:!m-0 max-sm:!h-auto max-sm:!max-h-[var(--sheet-partial-max-h,85dvh)] max-sm:!w-full max-sm:!max-w-none max-sm:!rounded-b-none"),
            // A `size` is set via inline maxWidth; keep mobile full-screen by
            // dropping that cap below `sm` (also covered above; kept explicit).
            size && size !== "full" && "max-sm:!max-w-none",
            // `full`: fill the viewport but stay a FLOATING rounded card on
            // desktop — a small inset margin all round, keeping the base
            // `rounded-2xl` (we deliberately don't override radius here). Mobile
            // is still edge-to-edge + sharp via the `max-sm:` block above, which
            // is what normal sheets do too. `!important` on the `sm:` utilities
            // because the per-side `data-[side=…]:sm:*` rules (right-4/left-auto,
            // w-3/4, max-w-sm) out-specify a plain utility — without it,
            // `size="full"` collapsed to the narrow side-drawer on desktop.
            size === "full" &&
              "sm:!inset-4 sm:!h-auto sm:!w-auto sm:!max-w-none",
            // Opt-in only: scroll-edge fade at the sheet's bottom (skipped when a
            // pinned footer is present — the footer owns the bottom edge and its
            // own fade), plus bottom clearance on the scroll body so the last
            // content can scroll clear of the fade rather than sitting under it.
            scrollFade &&
              "[&_[class*=overflow-y-]]:pb-10 not-has-[[data-slot=sheet-footer]]:after:pointer-events-none not-has-[[data-slot=sheet-footer]]:after:absolute not-has-[[data-slot=sheet-footer]]:after:inset-x-0 not-has-[[data-slot=sheet-footer]]:after:bottom-0 not-has-[[data-slot=sheet-footer]]:after:z-20 not-has-[[data-slot=sheet-footer]]:after:h-10 not-has-[[data-slot=sheet-footer]]:after:rounded-b-2xl not-has-[[data-slot=sheet-footer]]:after:bg-[image:var(--sheet-fade-up)]",
            className,
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <SheetPrimitive.Close
              data-slot="sheet-close"
              render={
                <Button
                  variant="ghost"
                  className={cn(
                    "absolute top-3 right-3 z-50",
                    // `solid`: paint the X on the sheet's own surface so it
                    // stays legible over content, with a hairline for
                    // separation on a pale image. Written as `var()` arbitrary
                    // values rather than `bg-background`/`text-foreground`
                    // because consumer apps remap the `background` colour NAME
                    // (booking's preset points it at the venue's brand
                    // `--background-color`) — the arbitrary value always
                    // resolves to the v4 surface token, same reasoning as the
                    // inline `backgroundColor` on the popup above. Hover and
                    // active are set explicitly so the surface never falls
                    // back to the ghost variant's translucent tint.
                    closeButtonVariant === "solid" &&
                      "border border-black/10 bg-[var(--background)] text-[var(--foreground)] hover:bg-[var(--accent)] active:bg-[var(--muted)] dark:border-white/10",
                    closeButtonClassName,
                  )}
                  size="icon-sm"
                />
              }
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Popup>
      </SheetPortal>
    </ScrollFadeContext.Provider>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  const scrollFade = React.useContext(ScrollFadeContext)
  return (
    <div
      data-slot="sheet-header"
      // With `scrollFade`, a soft fade below the header so scrolling content
      // dissolves into the surface as it passes under the title.
      className={cn(
        "flex flex-col gap-0.5 p-4",
        scrollFade &&
          "relative after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:z-20 after:h-10 after:bg-[image:var(--sheet-fade-down)]",
        className,
      )}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  const scrollFade = React.useContext(ScrollFadeContext)
  return (
    <div
      data-slot="sheet-footer"
      // With `scrollFade`, a matching fade above a pinned footer so content
      // dissolves into the footer surface instead of hard-cutting.
      className={cn(
        "mt-auto flex flex-col gap-2 p-4",
        scrollFade &&
          "relative before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:z-20 before:h-10 before:bg-[image:var(--sheet-fade-up)]",
        className,
      )}
      {...props}
    />
  )
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        "font-heading text-base font-medium text-foreground",
        className,
      )}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
